import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Sparkles, Palette, Package, ChevronDown, ChevronUp, Loader2, Share2, Camera } from 'lucide-react';
import AppFooter from '../components/AppFooter';
import { resolveImageUrl } from '../utils/imageUrl';

import { BACKEND_URL } from '../config/api';

const SharedDesignPage = () => {
  const { shareId } = useParams();
  const [design, setDesign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showInstructions, setShowInstructions] = useState(false);

  useEffect(() => {
    const fetchDesign = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/design/shared/${shareId}`);
        if (!res.ok) {
          if (res.status === 404) throw new Error('not_found');
          throw new Error('Failed to load design');
        }
        const data = await res.clone().json();
        setDesign(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchDesign();
  }, [shareId]);

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: design?.title || 'Craft Design',
          text: `Check out this craft design: ${design?.title}`,
          url: window.location.href,
        });
      } catch {}
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="animate-spin mx-auto text-sage mb-4" size={40} />
          <p className="text-gray-soft">Loading design...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-cream flex flex-col">
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="text-center max-w-md">
            <Sparkles className="mx-auto text-sage mb-4" size={48} />
            <h1 className="text-2xl font-light text-charcoal mb-3">
              {error === 'not_found' ? 'Design Not Found' : 'Something Went Wrong'}
            </h1>
            <p className="text-gray-soft mb-6">
              {error === 'not_found'
                ? 'This design may have been removed or sharing was turned off by the creator.'
                : 'We had trouble loading this design. Please try again later.'}
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-sage text-white rounded-xl hover:bg-sage-dark transition-colors"
              data-testid="shared-design-cta"
            >
              <Sparkles size={18} />
              Discover Kura Stash
            </Link>
          </div>
        </div>
        <AppFooter variant="minimal" />
      </div>
    );
  }

  const hasColors = design.color_scheme?.length > 0;
  const hasItems = design.items_needed?.length > 0;
  const hasInstructions = design.assembly_instructions?.length > 0;
  const hasVideos = design.video_recommendations?.length > 0;
  const hasImage = !!design.inspiration_image_url;
  const hasFinishedPhoto = !!design.finished_photo_url;

  return (
    <div className="min-h-screen bg-cream flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-beige">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <Sparkles className="text-sage" size={22} />
            <span className="text-lg font-light text-charcoal">Kura Stash</span>
          </Link>
          {navigator.share && (
            <button
              onClick={handleNativeShare}
              className="p-2 text-gray-soft hover:text-sage rounded-lg hover:bg-cream transition-colors"
              data-testid="shared-design-share-btn"
            >
              <Share2 size={20} />
            </button>
          )}
        </div>
      </header>

      {/* Content */}
      <main className="flex-1">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
          {/* Creator attribution */}
          <p className="text-sm text-gray-soft mb-2" data-testid="shared-design-creator">
            Designed by <span className="font-medium text-charcoal">{design.creator_name}</span>
          </p>

          {/* Title */}
          <h1 className="text-3xl sm:text-4xl font-light text-charcoal mb-3" data-testid="shared-design-title">
            {design.title}
          </h1>

          {/* Lifecycle badge */}
          {design.lifecycle_state === 'shared' && hasFinishedPhoto && (
            <div className="mb-4">
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full bg-amber-100 text-amber-700 border border-amber-200" data-testid="shared-lifecycle-badge">
                <Camera size={12} />
                Finished Creation
              </span>
            </div>
          )}

          {/* Description */}
          <p className="text-base text-gray-soft leading-relaxed mb-8" data-testid="shared-design-description">
            {design.description}
          </p>

          {/* Finished photo — the user's actual creation */}
          {hasFinishedPhoto && (
            <div className="mb-8" data-testid="shared-finished-photo-section">
              <h2 className="text-sm font-medium text-charcoal uppercase tracking-wide mb-3 flex items-center gap-2">
                <Camera size={16} className="text-amber-500" />
                The Finished Creation
              </h2>
              <div className="rounded-xl overflow-hidden bg-white border-2 border-amber-200 shadow-sm">
                <img
                  src={resolveImageUrl(design.finished_photo_url)}
                  alt="Finished creation"
                  className="w-full max-h-[500px] object-contain"
                  data-testid="shared-finished-photo"
                />
              </div>
            </div>
          )}

          {/* AI Inspiration image */}
          {hasImage && (
            <div className="mb-8 rounded-xl overflow-hidden bg-white border border-beige shadow-sm">
              {hasFinishedPhoto && (
                <div className="px-4 pt-3">
                  <p className="text-xs text-gray-soft uppercase tracking-wide">AI Design Visualization</p>
                </div>
              )}
              <img
                src={resolveImageUrl(design.inspiration_image_url)}
                alt={design.title}
                className="w-full max-h-[500px] object-contain"
                data-testid="shared-design-image"
              />
            </div>
          )}

          {/* Color scheme */}
          {hasColors && (
            <div className="bg-white rounded-xl border border-beige p-5 mb-5">
              <div className="flex items-center gap-3 mb-3">
                <Palette size={18} className="text-sage" />
                <h2 className="text-sm font-medium text-charcoal uppercase tracking-wide">Color Palette</h2>
              </div>
              <div className="flex gap-3">
                {design.color_scheme.map((color, idx) => (
                  <div key={idx} className="flex flex-col items-center gap-1.5">
                    <div
                      className="w-12 h-12 rounded-full border-2 border-white shadow-md"
                      style={{ backgroundColor: color }}
                      title={color}
                    />
                    <span className="text-[10px] text-gray-light font-mono">{color}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Materials needed */}
          {hasItems && (
            <div className="bg-white rounded-xl border border-beige p-5 mb-5">
              <div className="flex items-center gap-3 mb-3">
                <Package size={18} className="text-sage" />
                <h2 className="text-sm font-medium text-charcoal uppercase tracking-wide">Materials Needed</h2>
              </div>
              <div className="space-y-2">
                {design.items_needed.map((item, idx) => (
                  <div
                    key={idx}
                    className="flex items-start gap-3 p-3 bg-cream rounded-lg"
                    data-testid={`shared-item-${idx}`}
                  >
                    <div className="w-8 h-8 rounded-md bg-sage-light flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Package size={14} className="text-sage" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-charcoal">{item.name}</p>
                      {item.category && (
                        <span className="text-xs text-gray-soft capitalize">{item.category.replace('_', ' ')}</span>
                      )}
                      {item.description && (
                        <p className="text-xs text-gray-soft mt-0.5">{item.description}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Assembly instructions */}
          {hasInstructions && (
            <div className="bg-white rounded-xl border border-beige p-5 mb-5">
              <button
                onClick={() => setShowInstructions(!showInstructions)}
                className="flex items-center justify-between w-full text-left"
                data-testid="shared-toggle-instructions"
              >
                <h2 className="text-sm font-medium text-charcoal uppercase tracking-wide">
                  Assembly Instructions ({design.assembly_instructions.length} steps)
                </h2>
                {showInstructions ? <ChevronUp size={18} className="text-gray-soft" /> : <ChevronDown size={18} className="text-gray-soft" />}
              </button>
              {showInstructions && (
                <ol className="mt-4 space-y-3">
                  {design.assembly_instructions.map((step, idx) => (
                    <li key={idx} className="flex gap-3 text-sm">
                      <span className="flex-shrink-0 w-6 h-6 bg-sage-light text-sage rounded-full flex items-center justify-center text-xs font-medium mt-0.5">
                        {idx + 1}
                      </span>
                      <span className="text-gray-soft leading-relaxed">{step}</span>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          )}

          {/* Video recommendations */}
          {hasVideos && (
            <div className="bg-white rounded-xl border border-beige p-5 mb-5">
              <h2 className="text-sm font-medium text-charcoal uppercase tracking-wide mb-3">Expert References</h2>
              <div className="space-y-2">
                {design.video_recommendations.map((video, idx) => (
                  <a
                    key={idx}
                    href={video.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-3 bg-cream rounded-lg hover:bg-beige transition-colors"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" className="text-red-500 flex-shrink-0">
                      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814z"/>
                      <path d="M9.545 15.568V8.432L15.818 12l-6.273 3.568z" fill="white"/>
                    </svg>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-charcoal truncate">{video.title}</p>
                      <p className="text-xs text-gray-soft">{video.creator}</p>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Made with Kura Stash CTA */}
          <div className="bg-white rounded-xl border-2 border-sage/30 p-8 text-center mt-8 mb-4" data-testid="shared-design-cta-card">
            <Sparkles className="mx-auto text-sage mb-3" size={32} />
            <h3 className="text-xl font-medium text-charcoal mb-2">
              Made with Kura Stash
            </h3>
            <p className="text-sm text-gray-soft max-w-md mx-auto mb-5">
              Organize your craft supplies, get AI-powered design ideas, and bring your creative vision to life.
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-8 py-3 bg-sage text-white rounded-xl hover:bg-sage-dark transition-colors font-medium"
              data-testid="shared-design-signup-btn"
            >
              <Sparkles size={18} />
              Start Free Today
            </Link>
          </div>
        </div>
      </main>

      <AppFooter variant="minimal" />
    </div>
  );
};

export default SharedDesignPage;
